import {
  put,
  delay,
  takeEvery,
  all,
  call,
  select,
  race,
  cancelled,
} from "redux-saga/effects";
import _ from "lodash";
import moment from "moment-timezone";
import {
  setPageTopLoader,
  setPictreePhotoShootByAssetTepco,
  setPictreePhotoShootTrendByAssetTepco
} from "actions";
import { getJwtToken, getDataFromRDS, getDataFromSxi } from "api";
import { PICTREE, ENDPOINT } from "../../constants";
import assetConfig from "../../utils/pictree/AssetConfig";

const SXI_HEAVY_RETRY = {
  timeoutMs: 30000, // 30s
  retries: 3, // 合計 4 回までトライ（初回  リトライ3）
  backoffBase: 1500, // 1.5s指数バックオフ
};

const USERS_LIGHT_RETRY = {
  timeoutMs: 15000, // 15s
  retries: 1,
};

// タイムアウト＋リトライ
function* callWithTimeoutAndRetry(fn, args = [], opts = {}) {
  const {
    timeoutMs = 20000, // 1回の待ち時間（APIGW上限より少し短く）
    retries = 2, // 失敗時の再試行回数（合計3回＝初回＋2リトライ）
    backoffBase = 1000, // 1秒
    backoffFactor = 2, // 2倍ずつ
    jitter = true, // 少しランダムに待つ
    isRetryable = (err) => {
      const msg = (err && (err.message || err.errorMessage)) || "";
      return /timeout|timed out|504|gateway\s*time-?out/i.test(msg);
    },
  } = opts;

  let attempt = 0;
  while (true) {
    attempt = 1;

    try {
      // 1回の試行にタイムアウトをかける
      const { response, timeout } = yield race({
        response: call(fn, ...args),
        timeout: delay(timeoutMs),
      });

      if (timeout) throw new Error(`timeout after ${timeoutMs}ms`);
      return response; // 成功
    } catch (err) {
      if (attempt > retries || !isRetryable(err)) {
        throw err; // もう無理/リトライ不要
      }
      // 待ってから再試行（指数＋ジッター）
      const wait = Math.min(
        backoffBase * Math.pow(backoffFactor, attempt - 1),
        10000
      );
      const jitterMs = jitter ? Math.floor(Math.random() * (wait * 0.3)) : 0;
      yield delay(wait + jitterMs);
      // 次のループで再試行
    } finally {
      if (yield cancelled()) {
        // もし fetch/axios なら AbortController を仕込むとさらに良い
      }
    }
  }
}

function* runEffectsInBatches(effects, { concurrency = 2, gapMs = 300 } = {}) {
  const results = [];
  for (let i = 0; i < effects.length; i += concurrency) {
    const batch = effects.slice(i, i + concurrency);
    // 同時に投げるのは batch サイズ分だけ
    const batchResults = yield all(batch);
    results.push(...batchResults);

    // 次バッチの前にクールダウン（最後のバッチは不要）
    if (i + concurrency < effects.length && gapMs > 0) {
      yield delay(gapMs);
    }
  }
  return results;
}

const createApprovedBySubtypeQuery = ({ startUtc, endUtc, seasonQuery }) => {
  return {
    query: `
      SELECT pa.type, pa.sub_type, COUNT(DISTINCT rr.power_asset_id) AS reviewOkcnt
      FROM review_request_event rre
      JOIN review_request rr ON rre.review_request_id = rr.id
      JOIN power_asset pa ON pa.id = rr.power_asset_id
      WHERE ${seasonQuery}
      AND rre.type = 'APPROVED'
      AND rre.occurred_datetime >= '${startUtc}'
      AND rre.occurred_datetime <  '${endUtc}'
      GROUP BY pa.type, pa.sub_type
      ORDER BY reviewOkcnt DESC;
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

const createApprovedBySubtypeDailyQuery = ({ startUtc, endUtc, seasonQuery }) => {
  return {
    query: `
      SELECT
        DATE(CONVERT_TZ(rre.occurred_datetime, '+00:00', '+09:00')) AS day_jst,
        pa.sub_type,
        COUNT(DISTINCT rr.power_asset_id) AS reviewOkcnt
      FROM review_request_event rre
      JOIN review_request rr ON rre.review_request_id = rr.id
      JOIN power_asset pa ON pa.id = rr.power_asset_id
      WHERE ${seasonQuery}
        AND rre.type = 'APPROVED'
        AND rre.occurred_datetime >= '${startUtc}'
        AND rre.occurred_datetime <  '${endUtc}'
      GROUP BY day_jst, pa.sub_type
      ORDER BY day_jst ASC;
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

const enumerateDaysJst = (startJst, endJst) => {
  const days = [];
  let cur = moment.tz(startJst, "Asia/Tokyo");
  const end = moment.tz(endJst, "Asia/Tokyo");
  while (cur.isSameOrBefore(end, "day")) {
    days.push(cur.format("YYYY-MM-DD"));
    cur = cur.add(1, "day");
  }
  return days;
};

function transformDailyPayload(payload, subtypeMap) {
  const result = { date: [] };
  const temp = {};

  // initialize temporary arrays for all possible subtypes
  Object.entries(subtypeMap).forEach(([subType, label]) => {
    temp[label] = [];
  });

  // fill arrays
  payload.forEach((row) => {
    result.date.push(row.date);
    Object.entries(subtypeMap).forEach(([subType, label]) => {
      temp[label].push(row[subType] ?? 0);
    });
  });

  // filter out all-zero arrays
  Object.entries(temp).forEach(([label, arr]) => {
    if (arr.some((v) => v > 0)) {
      result[label] = arr;
    }
  });

  return [result];
}

export const currentPage = (state) => state.page.currentPage;
export const dateRange = (state) => state.page.dateRangePicTree;
export const selectedSeason = (state) => state.pictree.selectedSeason;

export function* getPictreeDataPhotoShootByAsset(action) {
  try {
    yield put(setPageTopLoader(true));

    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRange);
    const selectedSeasonData = yield select(selectedSeason);
    const seasonQuery = (() => {
      if (
        selectedSeasonData.length > 0 &&
        selectedSeasonData[0].seasonId !== undefined
      ) {
        const ids = selectedSeasonData.map((s) => `'${s.seasonId}'`).join(", ");
        return selectedSeasonData.length === 1
          ? `rre.game_space_id = ${ids}` // ids は "'id1'"
          : `rre.game_space_id IN (${ids})`; // ids は "'id1','id2',…"
      }
      return "";
    })();

    const startUtc = moment
      .tz(`${rangeTarget[0]} 00:00:00`, "Asia/Tokyo")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");
    const endUtc = moment
      .tz(`${rangeTarget[1]} 00:00:00`, "Asia/Tokyo")
      .add(1, "day")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");

    // プレースホルダ
    yield put(
      setPictreePhotoShootByAssetTepco([
        { subType: "loading", CNT: 0, label: "-" },
      ])
    );

    const singleSql = createApprovedBySubtypeQuery({
      startUtc,
      endUtc,
      seasonQuery,
    });

    const sxiEffects = [
      call(
        callWithTimeoutAndRetry,
        getDataFromRDS,
        [singleSql, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
        SXI_HEAVY_RETRY
      ),
    ];

    const [rows] = yield call(runEffectsInBatches, sxiEffects, {
      concurrency: 2,
      gapMs: 300,
    });

    // ---- 整形：type優先順 + 同type内は通常先 + 件数降順 ----
    const labelMap = assetConfig.assetSubType || {};
    const typeRank = { POWER_POLE: 1, MAN_HOLE: 2, HAND_HOLE: 3 };
    const isLucky = (subType) => (subType || "").includes("LUCKY");
    const getLabel = (type, subType) => {
      if (type === "MAN_HOLE" && subType === "NORMAL") return "マンホール";
      if (type === "HAND_HOLE" && subType === "NORMAL") return "ハンドホール";
      return labelMap[subType] || subType || `${type}`;
    };

    const payload = (rows || [])
      .map(({ type, sub_type, reviewOkcnt }) => ({
        type: type || "UNKNOWN",
        subType: sub_type,
        CNT: Number(reviewOkcnt) || 0,
        label: getLabel(type, sub_type),
      }))
      .filter((item) => item.CNT > 0)
      .sort((a, b) => {
        const rankDiff = (typeRank[a.type] || 99) - (typeRank[b.type] || 99);
        if (rankDiff !== 0) return rankDiff;
        const luckyDiff = Number(isLucky(a.subType)) - Number(isLucky(b.subType));
        if (luckyDiff !== 0) return luckyDiff;
        return b.CNT - a.CNT;
      })
      .map(({ type, subType, CNT, label }) => ({ type, subType, CNT, label }));

    yield put(setPictreePhotoShootByAssetTepco(payload));
  } catch (err) {
    console.log("[getPictreeDataPhotoShootByAsset] ERROR", err);
  } finally {
    yield put(setPageTopLoader(false));
  }
}

export function* getPictreeDataPhotoShootTrendByAsset(action) {
  try {
    yield put(setPageTopLoader(true));

    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRange);          // ["YYYY-MM-DD","YYYY-MM-DD"]（JST）
    const selectedSeasonData = yield select(selectedSeason);

    // season WHERE
    const seasonQuery = (() => {
      if (selectedSeasonData.length > 0 && selectedSeasonData[0].seasonId !== undefined) {
        const ids = selectedSeasonData.map((s) => `'${s.seasonId}'`).join(", ");
        return selectedSeasonData.length === 1
          ? `rre.game_space_id = ${ids}`
          : `rre.game_space_id IN (${ids})`;
      }
      return "";
    })();

    // JST日の境界 → UTC へ
    const startUtc = moment.tz(`${rangeTarget[0]} 00:00:00`, "Asia/Tokyo").utc().format("YYYY-MM-DD HH:mm:ss");
    const endUtc   = moment.tz(`${rangeTarget[1]} 00:00:00`, "Asia/Tokyo").add(1, "day").utc().format("YYYY-MM-DD HH:mm:ss");

    const dailySql = createApprovedBySubtypeDailyQuery({ startUtc, endUtc, seasonQuery });

    const [rows] = yield call(runEffectsInBatches, [
      call(
        callWithTimeoutAndRetry,
        getDataFromRDS,
        [dailySql, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
        SXI_HEAVY_RETRY
      ),
    ]);

    // ---- 整形：日付×サブタイプ ----
    const subtypeKeys = Object.keys(assetConfig.assetSubType);
    const allDays = enumerateDaysJst(rangeTarget[0], rangeTarget[1]);

    // day => (subtype => count)
    const byDay = new Map();
    (rows || []).forEach(({ day_jst, sub_type, reviewOkcnt }) => {
      const dayKey = moment.tz(day_jst, "Asia/Tokyo").format("YYYY-MM-DD"); // ★ここ
      if (!byDay.has(dayKey)) byDay.set(dayKey, new Map());
      byDay.get(dayKey).set(sub_type, Number(reviewOkcnt) || 0);
    });

    // 出力： [{ date, NORMAL, WITH_PLANTS, HAS_PROTECTIVE_TUBE }]
    const payload = allDays.map((d) => {
      const row = { date: d };
      const cell = byDay.get(d) || new Map();
      subtypeKeys.forEach((k) => {
        row[k] = cell.get(k) || 0;
      });
      return row;
    });

    const payload2 = transformDailyPayload(payload, assetConfig.assetSubType);

    yield put(setPictreePhotoShootTrendByAssetTepco(payload2));
  } catch (err) {
    console.log("[getPictreeDataPhotoShootTrendByAsset] ERROR", err);
  } finally {
    yield put(setPageTopLoader(false));
  }
}

export default function* watchPageSettings() {
  yield takeEvery(PICTREE.GET_PHOTOSHOOT_BY_ASSET_TEPCO, getPictreeDataPhotoShootByAsset);
  yield takeEvery(PICTREE.GET_PHOTOSHOOT_TREND_BY_ASSET_TEPCO, getPictreeDataPhotoShootTrendByAsset);
}
